import React, { useEffect, useRef, useState, forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { SetState } from 'state/StateTypes';
import { InputChange } from '../../types';
import ErrorMessage from 'components/ErrorMessage';
import Asterisk from 'components/Asterisk';

type ValidationHandler = (value: string) => boolean;

interface Props {
   placeholder?: string;
   size: 'half' | 'full';
   label?: string;
   margin?: string;
   errorMessage?: string;
   validationHandler?: ValidationHandler;
   name?: string;
   disabled?: boolean;
   helperText?: string;
   value?: string;
   isValidHandler?: (isValid: boolean) => any;
   type?: string;
   onChange?: (event: InputChange) => SetState<string> | void;
   required?: boolean;
}

const Input = forwardRef(
   (
      {
         placeholder,
         size,
         label,
         margin,
         errorMessage,
         validationHandler,
         helperText = '',
         value = '',
         name,
         disabled = false,
         isValidHandler = () => true,
         type = 'text',
         onChange = () => {},
         required = false,
      }: Props,
      forwardedRef: Ref<HTMLInputElement>
   ) => {
      const inputRef = useRef<any>(null);
      const [focused, setFocus] = useState(false);
      const [onBlurError, setOnBlurError] = useState(false);
      const [onChangeError, setOnChangeError] = useState(false);
      const [unMaskPassword, setUnMaskPassword] = useState(false);

      // We track two types of errors, onBlur and onChange.

      // In general we want errors to only show once a user is done typing. We accomplish
      // this by handling the bulk of errors on blur.

      // Some errors, such as weak or common passwords, need to be handled while typing so the
      // user doesn't have to keep leaving the input to trigger the error. These errors
      // we handle in onChange.

      const onBlurIsValid = validationHandler || onBlurIsValidHandlerByType(type, required);
      const onBlurErrorMessage = errorMessage || (label && onBlurErrorMessageByType(type, label));

      const isInitialMount = useRef(true);
      useEffect(() => {
         // We don't want to accidentally mark inputs as valid when we mount.
         // Errors are initialized to false so the user isn't staring at a new form with
         // a bunch of errors. This means if we call the handler on mount it will set
         // everything as being error free and that's not necessarily true!
         //
         // However, if the consumer provides a default value (i.e. value !== '') we do want to tirgger the handler
         // as most consumers depend on this to determine if a form can be submitted.
         if (isInitialMount.current) {
            isInitialMount.current = false;
            if (value !== '') {
               isValidHandler(onBlurIsValid(value));
            }
         } else if (!isInitialMount.current) {
            isValidHandler(type === 'set-password' ? !onBlurError && !onChangeError : !onBlurError);
         }
      }, [value, onChangeError, onBlurError]);

      function onBlurValidation(value: string) {
         setOnBlurError(!onBlurIsValid(value));
      }

      function onChangeValidation(value: string) {
         // Errors *must* be accurate, so if the user solves an onBlur error
         // we go ahead and remove it to prevent confusion
         if (onBlurIsValid(value)) {
            setOnBlurError(false);
         }
      }

      let inputType = type === 'set-password' ? 'password' : type;
      if (inputType === 'password' && unMaskPassword) {
         inputType = 'text';
      }

      // figure out the correct DOM event type for this
      function handleMasking(e: any) {
         e.stopPropagation();
         inputRef.current.focus();
         setUnMaskPassword(!unMaskPassword);
      }

      return (
         <InputLabelWrapper>
            {label && (
               <Label htmlFor={name} visible={!onBlurError}>
                  {label}
                  {required && <Asterisk />}
                  {helperText && ` - ${helperText}`}
               </Label>
            )}
            {(onBlurError || onChangeError) && (
               <StyledErrorMessage as="label">
                  {onBlurError && onBlurErrorMessage}
                  {onChangeError && ' Your password is too common or easy to guess'}
               </StyledErrorMessage>
            )}
            <InputWrapper size={size}>
               <StyledInput
                  name={name}
                  value={value}
                  ref={forwardedRef || inputRef}
                  placeholder={focused ? '' : placeholder}
                  type={inputType}
                  disabled={disabled}
                  margin={margin}
                  required={required}
                  onFocus={() => setFocus(true)}
                  onBlur={e => {
                     setFocus(false);
                     onBlurValidation(e.target.value);
                  }}
                  onChange={e => {
                     onChangeValidation(e.target.value);
                     onChange(e);
                  }}
               />
               {type.includes('password') && value !== '' && renderMaskToggle(unMaskPassword, handleMasking)}
            </InputWrapper>
         </InputLabelWrapper>
      );
   }
);

const renderMaskToggle = (unMaskPassword: boolean, handleMasking: (e: any) => any) => {
   const text = !unMaskPassword ? 'SHOW' : 'HIDE';
   return (
      <MaskToggle type="button" onClick={handleMasking}>
         {text}
      </MaskToggle>
   );
};

const onBlurIsValidHandlerByType = (type: string, required: boolean): ValidationHandler => {
   if (!required) {
      return () => {
         return true;
      };
   }
   switch (type) {
      case 'email':
         return (value: string) => value.includes('@') && value.includes('.');
      case 'set-password':
         return (value: string) => value.length >= 8;
      case 'text':
      case 'password':
      case 'tel':
         return (value: string) => !!value;
      default:
         return () => {
            return true;
         };
   }
};

const onBlurErrorMessageByType = (type: string, label: string) => {
   switch (type) {
      case 'email':
         return 'Please enter a valid email address.';
      case 'text':
      case 'password':
      case 'tel':
         return `${label} is required.`;
      case 'set-password':
         return 'Your password must be at least 8 characters long.';
      default:
         return '';
   }
};

const halfSize = 200;
const fullSize = 400;

const InputLabelWrapper = styled.div`
   margin: 0 auto;
   position: relative;
`;

const InputWrapper = styled.div`
   margin: 1em auto;
   position: relative;
   max-width: ${({ size = 'full' }: { size: 'half' | 'full' }) => (size === 'half' ? `${halfSize}` : `${fullSize}`)}px;
   width: 100%;
   display: inline-block;
`;
const Label = styled.label`
   font-weight: 600;
   font-size: 1.2em;
   display: ${({ visible }: { visible: boolean }) => (visible ? 'block' : 'none')};
   position: absolute;
   left: 75px;
   top: -20px;
   color: ${({ theme }) => theme.primaryBlueDark};
   @media only screen and (max-width: 415px) {
      left: 10px;
   }
`;

const StyledErrorMessage = styled(ErrorMessage)`
   padding: 0 5px;
   height: 70px;
   width: auto;
   margin: unset;
   border: unset;
   box-shadow: unset;
   font-weight: thin;
   font-size: unset;
   position: absolute;
   left: 60px;
   top: -40px;

   @media only screen and (min-width: 600px) {
      left: 50px;
   }
`;

const MaskToggle = styled.button`
   font-size: 14px;
   color: ${({ theme }) => theme.secondaryBlueDark};
   font-weight: bold;
   position: absolute;
   right: 20px;
   top: 0.8em;
   margin: 0;
   padding: 3px 5px;
   border: none;
   background: transparent;
   cursor: pointer;
   user-select: none;

   :focus,
   &::-moz-focus-inner {
      background: ${({ theme }) => theme.secondaryWhite};
      color: ${({ theme }) => theme.almostBlack};
      border-radius: 3px;
      border: none;
      text-decoration: underline;
   }
`;

const StyledInput = styled.input.attrs(({ type }) => ({
   type,
}))`
   font-size: 0.9em;
   color: ${({ theme }) => theme.almostBlack};
   background: white;
   width: 100%;
   height: 28px;
   margin: ${({ margin }: { margin: string | undefined }) => (margin ? margin : '2px 0 35px 0')};
   border: 1px solid ${({ theme }) => theme.primaryGrey};
   box-sizing: border-box;
   padding: 20px;
   border-radius: 10px;
   ::placeholder {
      color: ${({ theme }) => theme.primaryGrey};
      opacity: 0.9;
      font-style: italic;
   }
`;

export default Input;
